"use client";

import { Search, LayoutGrid, List } from "lucide-react";
import { useTranslations } from "next-intl";

import { TournamentStatus } from "@/entities/tournament/model/types";
import { Button } from "@/shared/ui/buttons/button";
import { Input } from "@/shared/ui/inputs/input";
import { Checkbox } from "@/shared/ui/inputs/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { ScrollableTagStrip } from "@/shared/ui/inputs/scrollable-tag-strip";

export type ViewMode = "grid" | "list";
export type SortByType = "startDate" | "prizePool" | "participants";

export type StatusFilterValue = TournamentStatus | "all" | "Archive";

interface TournamentFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: StatusFilterValue;
  setStatusFilter: (status: StatusFilterValue) => void;
  isSiteAdmin?: boolean;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  sortBy: SortByType;
  setSortBy: (sort: SortByType) => void;
  withFriends?: boolean;
  setWithFriends?: (value: boolean) => void;
  isAuthenticated?: boolean;
}

function getFilterButtons(isSiteAdmin: boolean, t: ReturnType<typeof useTranslations<"Tournaments.filters">>): {
  label: string;
  value: StatusFilterValue;
}[] {
  return [
    { label: t("all"), value: "all" },
    { label: t("inProgress"), value: "InProgress" },
    { label: t("upcoming"), value: "Upcoming" },
    { label: t("registrationOpen"), value: "RegistrationOpen" },
    { label: t("registrationClosed"), value: "RegistrationClosed" },
    // Архив прошлых турниров публичен (эндпоинт AllowAnonymous) — чип для всех.
    { label: t("archive"), value: "Archive" as const },
  ];
}

export const TournamentFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  isSiteAdmin = false,
  viewMode,
  setViewMode,
  sortBy,
  setSortBy,
  withFriends = false,
  setWithFriends,
  isAuthenticated = false,
}: TournamentFiltersProps) => {
  const t = useTranslations("Tournaments.filters");
  const filterButtons = getFilterButtons(isSiteAdmin, t);

  return (
    <div className="mb-4 sm:mb-8">
      {/* md:items-start, а не items-center: слева колонка из двух рядов (поиск и
          чипсы статусов), и при центрировании сортировка с переключателем вида
          повисали между ними, а не в линию с полем поиска. */}
      <div className="flex flex-col md:flex-row gap-3 sm:gap-4 md:items-start justify-between">
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            {isAuthenticated && setWithFriends && (
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <Checkbox
                  checked={withFriends}
                  onCheckedChange={(v) => setWithFriends(v === true)}
                />
                <span className="text-sm">{t("withFriends")}</span>
              </label>
            )}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t("searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 pl-10"
              />
            </div>
          </div>
          <ScrollableTagStrip ariaLabel={t("statusStripAria")}>
            {filterButtons.map(({ label, value }) => (
              <Button
                key={value}
                variant={statusFilter === value ? "default" : "outline"}
                size="sm"
                className="shrink-0 rounded-full"
                onClick={() => setStatusFilter(value)}
              >
                {label}
              </Button>
            ))}
          </ScrollableTagStrip>
          {/* Search tag chips (LoL/Cup/Draft/Weekly/Solo) убраны по UX-запросу —
              в реальной выдаче эти теги не помогали фильтровать (только
              шумели). Если в будущем понадобится — вернуть searchTagKeys
              + map. Локали `searchTags.tagLol|tagCup|...` остаются. */}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <Select
            value={sortBy}
            onValueChange={(value: SortByType) => setSortBy(value)}
          >
            <SelectTrigger className="w-full md:w-[180px] h-9" aria-label={t("sortPlaceholder")}>
              <SelectValue placeholder={t("sortPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="startDate">{t("sortBy.startDate")}</SelectItem>
              <SelectItem value="prizePool">{t("sortBy.prizePool")}</SelectItem>
              <SelectItem value="participants">
                {t("sortBy.participants")}
              </SelectItem>
            </SelectContent>
          </Select>

          <div className="hidden md:flex items-center space-x-1 border border-input rounded-md p-1 h-9">
            <Button
              variant={viewMode === "grid" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("grid")}
              aria-label={t("viewGrid")}
              aria-pressed={viewMode === "grid"}
              className="h-7 w-7"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
              aria-label={t("viewList")}
              aria-pressed={viewMode === "list"}
              className="h-7 w-7"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
